import { useMemo, useEffect, useState } from 'react';

import { t } from '@lingui/macro';

import PerformanceChart from '../performance-chart/PerformanceChart';
import { BuiltInTooltip } from '../tooltip/types';
import { SyslogDialog } from '../../dialogs';
import { useMetricColor, usePaletteType } from '../../../utilities/ColorUtilities';
import { FORMATTER_TYPES } from '../../../utilities/FormattingUtilities';
import { getPointWidth } from '../data-series';
import { useFilterControl } from '../../page-framework/filter/FilterControl';
import { useFilterState } from '../../../stores/FilterStateStore';
import { timeRangeFilterId } from '../../filters';

export function useSyslogDataSource(deviceType) {
    return useMemo(() => {
        return {
            url: '/query/chart',
            params: {
                type: deviceType,
                extended: true,
                metrics: [
                    'countEmerg',
                    'countAlert',
                    'countCrit',
                    'countErr',
                    'countWarning',
                    'countInformational',
                    'countNotice',
                    'countDebug',
                ],
            },
        };
    }, [deviceType]);
}

function useXAxis() {
    const palette = usePaletteType();
    return useMemo(() => {
        return {
            gridLineColor: palette === 'dark' ? '#171717' : '#FFFFFF',
        };
    }, [palette]);
}

export function useSyslogChartConfig(type) {
    const COLOR_EMERGENCY = useMetricColor('syslogEmergency');
    const COLOR_ALERT_CRITICAL = useMetricColor('syslogAlertCritical');
    const COLOR_ERROR = useMetricColor('syslogError');
    const COLOR_WARNING = useMetricColor('syslogWarning');
    const COLOR_NOTICE_INFO_DEBUG = useMetricColor('syslogNoticeInfoDebug');

    return useMemo(
        () => ({
            yAxis: [
                {
                    title: {
                        text: t`Count`,
                    },
                    type,
                    min: type === LINEAR ? 0 : 0.1,
                    allowDecimals: false,
                },
            ],
            chartType: 'column',
            stacking: 'normal',
            tooltip: BuiltInTooltip,
            showGranularity: true,
            allowLegendItemClick: false,
            seriesCursor: 'pointer',
            chartTrends: [
                {
                    formatter: FORMATTER_TYPES.LOG,
                    units: '',
                    key: 'countEmerg',
                    label: t`Emergency`,
                    color: COLOR_EMERGENCY,
                    multiplier: 1,
                },
                {
                    formatter: FORMATTER_TYPES.LOG,
                    units: '',
                    key: 'countAlertCritical',
                    aggregationKeys: ['countAlert', 'countCrit'],
                    label: t`Alert/Critical`,
                    color: COLOR_ALERT_CRITICAL,
                    multiplier: 1,
                },
                {
                    formatter: FORMATTER_TYPES.LOG,
                    units: '',
                    key: 'countErr',
                    label: t`Error`,
                    color: COLOR_ERROR,
                    multiplier: 1,
                },
                {
                    formatter: FORMATTER_TYPES.LOG,
                    units: '',
                    key: 'countWarning',
                    label: t`Warning`,
                    color: COLOR_WARNING,
                    multiplier: 1,
                },
                {
                    formatter: FORMATTER_TYPES.LOG,
                    units: '',
                    key: 'countNoticeInfoDebug',
                    aggregationKeys: ['countNotice', 'countInformational', 'countDebug'],
                    label: t`Notice/Informational/Debug`,
                    color: COLOR_NOTICE_INFO_DEBUG,
                    multiplier: 1,
                },
            ],
            legendCursor: false,
            plotOptions: {
                column: {
                    cursor: 'pointer',
                    states: {
                        hover: {
                            brightness: 0.1,
                        },
                    },
                },
            },
        }),
        [COLOR_ALERT_CRITICAL, COLOR_EMERGENCY, COLOR_ERROR, COLOR_NOTICE_INFO_DEBUG, COLOR_WARNING, type]
    );
}

export const LINEAR = 'linear';
export const LOGARITHMIC = 'logarithmic';

export function SyslogChart(props) {
    const { chartDef, data: chartData, entity } = props;
    const xAxis = useXAxis();
    const filterControl = useFilterControl();
    const [widths, setWidths] = useState({});
    const [pointWidth, setPointWidth] = useState(0);
    const [open, setOpen] = useState(false);
    const [timeRange, setTimeRange] = useState({});
    const [filterState] = useFilterState();
    const filterStateTimeRange = filterState?.current?.[timeRangeFilterId];
    const timeRangeStart = filterStateTimeRange?.start;
    const timeRangeEnd = filterStateTimeRange?.end;
    const chartWidth = widths?.chart?.clipBox?.width || 0;

    // Calculate the pointWidth for the columns, setting a 1-pixel "gap" between them
    useEffect(() => {
        if (chartWidth && chartData) {
            const newPointWidth = getPointWidth(chartDef, chartData, chartWidth, {
                start: timeRangeStart,
                end: timeRangeEnd,
            });

            if (newPointWidth !== pointWidth && newPointWidth) {
                setPointWidth(newPointWidth);
            }
        }
    }, [pointWidth, chartDef, chartData, chartWidth, timeRangeStart, timeRangeEnd]);

    const callbacks = useMemo(() => {
        return {
            onClick: (event) => {
                if (timeRange?.start !== event.point.x) {
                    const start = event.point.x;
                    const end = start + event.granularity;
                    setTimeRange({ start, end });
                    // Open the event detail dialog
                    filterControl.pauseAutoRefresh();
                    setOpen(true);
                }
            },
        };
    }, [filterControl, timeRange]);

    function handleCloseDialog() {
        filterControl.resumeAutoRefresh();
        setOpen(false);
    }

    return (
        <>
            <PerformanceChart
                chartDef={chartDef}
                data={chartData}
                handleEvents={callbacks}
                onSetWidths={setWidths}
                pointWidth={pointWidth}
                xAxis={xAxis}
                hideLine
            />
            {timeRange && (
                <SyslogDialog open={open} onClose={handleCloseDialog} timeRange={timeRange} entity={entity} />
            )}
        </>
    );
}
